import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
// API
import { getCommissionCyclesCitCommissionPayout, getSystemSettingsCitCommissionPayout } from "@/Washouts/api/common/queries";
// Components
import EmployeeMetaSection from "@/Washouts/components/EmployeeMetaSection";
// MUI
import { Grid, GridProps } from "@mui/material";
// Tables
import SalesPerformanceTable from "@/Washouts/components/tables/Finance/Managers/SalesPerformanceTable";
import PendingDealsTable from "@/Washouts/components/tables/Finance/Managers/PendingDealsTable";
import CancellationTable from "@Washouts/components/tables/Finance/Managers/CancellationTable/CancellationTable.tsx";
import ChargebacksTable from "@/Washouts/components/tables/Common/ChargebacksTable";
import ProductConversionsTable, {
	getChargebacksFromRawConversionsData,
	processRawConversionsData,
} from "@/Washouts/components/tables/Finance/Managers/ProductConversionsTable";
import Compensation from "@/Washouts/components/tables/Common/CompensationTable";
import CITPerformanceTable from "@/Washouts/components/tables/Finance/Managers/CITPerformanceTable";
import CallActivityPerformanceWrapper, { getCallActivityData } from "@/Washouts/components/CallActivityPerformanceWrapper";
import SpiffsTable from "@/Washouts/components/tables/Sales/Associates/SpiffsTable";
// Types & Constants
import {
	ChargebacksRecord,
	ChargebacksTableData,
	ChargebacksWrapper,
	FinanceManagerWashoutContentProps,
	FinanceManagerWashoutContentSectionProps,
	ProductConversionData,
	Row,
} from "@/Washouts/types/finance";
import { useIsWashoutEditable } from "@/Washouts/hooks/WashoutReview/useIsWashoutEditable";
import { BASE_TABLE_PROPS, DEFAULT_TABLE_MAX_ROWS } from "@/Washouts/constants";
import { IWashoutModel } from "@/Washouts/types/commons";

import { nullishResolver } from "@/Common/helpers/sonar";

import { CardsDenseGrid } from "@/Washouts/layouts/CardsGrid";
import { resolveCardsData } from "@/Washouts/helpers/CardsGrid";
import { DeductionItem, LongevityInput, PerformanceMetricsInput } from "@/Washouts/types/CardsGrid";

const prepareChargebacksData = (preOwnedChargebacks?: ChargebacksWrapper, newChargebacks?: ChargebacksWrapper) => {
	const chargebacks: { total: number; chargebacks: ChargebacksRecord[] } = {
		total: 0,
		chargebacks: [],
	};
	chargebacks.total += preOwnedChargebacks?.chargebackCommission ?? 0;
	chargebacks.total += newChargebacks?.chargebackCommission ?? 0;
	for (let i = 0; i < (preOwnedChargebacks?.chargebacks?.length ?? 0); i++) {
		if (preOwnedChargebacks) {
			chargebacks.chargebacks.push(preOwnedChargebacks?.chargebacks[i]);
		}
	}
	for (let i = 0; i < (newChargebacks?.chargebacks?.length ?? 0); i++) {
		if (newChargebacks) {
			chargebacks.chargebacks.push(newChargebacks?.chargebacks[i]);
		}
	}
	return chargebacks;
};

function FullWidthTablesSection({
	model,
	stillLoading,
	citCommissionPayoutInitialPositions = [],
	systemSettingsCitCommissionPayout = [],
}: FinanceManagerWashoutContentSectionProps) {
	return (
		<>
			<Grid item>
				<SalesPerformanceTable
					divider
					data={model?.salesPerformance?.sales}
					columnVisibility={model?.salesPerformance?.columnVisibility}
					dataPresent={Boolean(model)}
					loading={stillLoading}
					tableProps={{ ...BASE_TABLE_PROPS, maxRows: 16 }}
				/>
			</Grid>

			<Grid item>
				<PendingDealsTable
					divider
					data={model?.salesLog?.sales}
					dataPresent={Boolean(model)}
					loading={stillLoading}
					tableProps={{ ...BASE_TABLE_PROPS }}
				/>
			</Grid>

			<Grid item>
				<CancellationTable
					title={"Cancellations"}
					divider
					data={model?.cancellations}
					dataPresent={Boolean(model)}
					loading={stillLoading}
					tableProps={{ ...BASE_TABLE_PROPS }}
				/>
			</Grid>

			{(citCommissionPayoutInitialPositions?.some((position) => position === model?.employee?.position) ||
				systemSettingsCitCommissionPayout?.some((position) => position === model?.employee?.position)) && (
				<Grid item>
					<SalesPerformanceTable
						title={"Carry Over Commissions"}
						emptyTitle={"Carry Over Commissions"}
						data={model?.carryOver?.sales}
						divider
						dataPresent={Boolean(model?.carryOver?.sales)}
						loading={stillLoading}
						aggregationToUse={"selectedSum"}
						totalsTypeTitle={model?.carryOver?.totals?.totalsTypeTitle}
						tableProps={{ ...BASE_TABLE_PROPS }}
					/>
				</Grid>
			)}
		</>
	);
}

function CallActivityAndCitSection({
	model,
	stillLoading,
	handleSummaryChange = () => {},
	...props
}: FinanceManagerWashoutContentSectionProps) {
	const citLength = model?.citPerformance?.cits.length ?? 0;
	const callActivityLength = getCallActivityData(model as IWashoutModel).length;
	const maxRows = Math.min(DEFAULT_TABLE_MAX_ROWS, Math.max(callActivityLength, citLength)) + 1;
	const enableRowsFiller = callActivityLength > 0 || citLength > 0;
	return (
		<Grid
			container
			direction="row"
			className={clsx({
				"pt-[15px] px-[30px] pb-[15px]": isAllReady(Boolean(model), stillLoading),
				"p-0": !isAllReady(Boolean(model), stillLoading),
			})}
		>
			<Grid item className="w-[646px]">
				<CallActivityPerformanceWrapper
					model={model}
					onActionUpdate={handleSummaryChange}
					loading={stillLoading}
					addTooltip
					editable={props.selected?.status === "ACTIVE" || props.selected?.status === "CLOSING"}
					dataPresent={Boolean(model)}
					tableProps={{ ...BASE_TABLE_PROPS, maxRows, enableRowsFiller }}
				/>
			</Grid>

			<Grid item className="w-[48px]" />

			<Grid item className="w-[646px]">
				{Boolean(model?.citPerformance) && (
					<CITPerformanceTable
						key={`${model?._id}-cit`}
						loading={stillLoading}
						dataPresent={Boolean(model)}
						data={model?.citPerformance}
						tableProps={{ ...BASE_TABLE_PROPS, maxRows, enableRowsFiller }}
					/>
				)}
			</Grid>
		</Grid>
	);
}

const GAP_ROWS_WHEN_SEPARATED = 3;
const MAX_ALLOWED_ROWS_TOP_TABLE = 4;
const MAX_ALLOWED_ROWS_BOTTOM_TABLE = 5;
const DEFAULT_ROWS_WHEN_EMPTY_DATA = 2;

function ConversionsAndChargeBacksSection({
	rawData,
	pcData,
	cbData,
	combined,
	chargeBackData,
	stillLoading,
	sharedId,
}: Readonly<{
	stillLoading: boolean;
	chargeBackData?: FinanceManagerWashoutContentSectionProps["chargeBackData"];
	rawData?: ProductConversionData;
	pcData?: Row[];
	cbData?: ChargebacksTableData[];
	combined?: boolean;
	sharedId?: string;
}>) {
	const loading = stillLoading;
	const combinedMaxRows = Math.min(
		Math.max(nullishResolver(pcData?.length, 0), nullishResolver(chargeBackData?.chargebacks.length, 0)),
		DEFAULT_TABLE_MAX_ROWS
	);
	const cbMaxRows =
		cbData?.length === 0 ? DEFAULT_ROWS_WHEN_EMPTY_DATA : Math.min(nullishResolver(cbData?.length, 0), MAX_ALLOWED_ROWS_TOP_TABLE);
	const pcMaxRows =
		pcData?.length === 0 ? DEFAULT_ROWS_WHEN_EMPTY_DATA : Math.min(nullishResolver(pcData?.length, 0), MAX_ALLOWED_ROWS_BOTTOM_TABLE);
	const separatedMaxRows = cbMaxRows + GAP_ROWS_WHEN_SEPARATED + pcMaxRows;
	const maxRows = (combined ? combinedMaxRows : separatedMaxRows) + 1;

	return (
		<Grid key={`${sharedId}-conversions-chargebacks`} container direction="row" className="pt-[15px] px-[30px] pb-[15px]">
			<Grid item className="w-[646px]">
				<ProductConversionsTable
					key={`${sharedId}-conversions-left`}
					rawData={rawData}
					combined={combined}
					data={pcData}
					cbData={cbData}
					dataPresent={Boolean(pcData)}
					loading={loading}
					maxSeparatedConversionsTopTableRows={cbMaxRows}
					maxSeparatedConversionsBottomTableRows={pcMaxRows}
					gapInRowsBetweenSeparatedTables={GAP_ROWS_WHEN_SEPARATED}
					tableProps={{
						...BASE_TABLE_PROPS,
						maxRows,
						enableRowsFiller: combined,
					}}
				/>
			</Grid>

			<Grid item className="w-[48px]" />

			<Grid item className="w-[646px]">
				<ChargebacksTable
					key={`${sharedId}-chargebacks-right`}
					data={chargeBackData?.chargebacks}
					staticFooter={Number(chargeBackData?.total ?? 0) || "-"}
					dataPresent={Boolean(chargeBackData)}
					loading={loading}
					hideFooter={true}
					tableProps={{
						...BASE_TABLE_PROPS,
						maxRows,
						enableRowsFiller: (chargeBackData?.chargebacks.length ?? 0) < maxRows,
					}}
				/>
			</Grid>
		</Grid>
	);
}

function CompensationSection({
	model,
	selected,
	stillLoading,
	overrideCompensation = false,
	handleSummaryChange = () => {},
}: FinanceManagerWashoutContentSectionProps) {
	const isEditable = useIsWashoutEditable({
		cycleStatus: selected?.status ?? "",
		positionStatus: model?.review?.status ?? "",
		hasRole: overrideCompensation,
	});

	return (
		<Grid item className="w-[646px]">
			<Compensation
				data={model?.summary?.summary ?? []}
				addedData={model?.addedCompensations ?? []}
				tooltip
				editable={isEditable}
				columns={[
					{
						field: "tier",
						headerName: "Tier",
						align: "left",
						headerAlign: "left",
					},
				]}
				itemsDefinitions={[
					{ item: "Chargebacks Gross Commission", type: "percentage" },
					{ item: "Variable Gross Commissions", type: "percentage" },
					{ item: "Miami Stores USCER-Bonus", type: "percentage" },
					{ item: "Inbound Missed Calls", type: "percentage" },
				]}
				dataPresent={Boolean(model)}
				loading={stillLoading}
				onActionUpdate={handleSummaryChange}
			/>
		</Grid>
	);
}

function isStillLoading(isLoading: boolean, isLoadingCommissionPayout: boolean, isLoadingSettingsCitCommission: boolean) {
	return isLoading || isLoadingCommissionPayout || isLoadingSettingsCitCommission;
}

function isAllReady(modelReady: boolean, stillLoading: boolean) {
	return modelReady || stillLoading;
}

function extractConversionData(model?: FinanceManagerWashoutContentProps["model"]) {
	const conversionsData = model?.productConversions
		? processRawConversionsData({ data: model?.productConversions, combined: Boolean(model?.productConversions.globalGrossAndUnits) })
		: undefined;
	const chargebacksData = model?.productConversions?.totals
		? getChargebacksFromRawConversionsData(model?.productConversions.totals)
		: undefined;
	return { pcData: conversionsData, cbData: chargebacksData };
}

const SpiffBonusSection = ({ model = undefined, isLoading = false }: Readonly<FinanceManagerWashoutContentProps & GridProps>) => {
	if (!model?.spiffs?.spiffs && !isLoading) {
		return null;
	}
	return (
		<Grid item className="w-[646px]">
			<SpiffsTable
				key={`${model?._id}-spiffs`}
				data={model?.spiffs?.spiffs ?? []}
				dataPresent={Boolean(model)}
				loading={isLoading}
				tableProps={{ ...BASE_TABLE_PROPS }}
			/>
		</Grid>
	);
};

export default function FinanceManagerWashoutContent(props: Readonly<FinanceManagerWashoutContentProps>) {
	const { model = undefined, selected = null, isLoading = false, hideEmployeeMeta = false, disableBoxShadow = false } = props;

	const chargeBackData = prepareChargebacksData(model?.preOwnedChargebacks, model?.newChargebacks);

	const { data: citCommissionPayoutInitialPositions, isLoading: isLoadingCommissionPayout } = useQuery({
		queryKey: ["citCommissionPayout", props.locationId, props.model?.commissionCycleId],
		queryFn: async () => {
			return (
				(await getCommissionCyclesCitCommissionPayout(props.locationId, props.model?.commissionCycleId))
					?.citCommissionPayoutInitialPositions ?? []
			);
		},
		enabled: !!props.locationId && !!props.model?.commissionCycleId,
		staleTime: Infinity,
	});

	const { data: systemSettingsCitCommissionPayout, isLoading: isLoadingSettingsCitCommission } = useQuery({
		queryKey: ["systemSettingsCitCommissionPayout", "finance", props?.positionType, props.locationId],
		queryFn: async () => {
			return (await getSystemSettingsCitCommissionPayout(props.locationId))?.citCommissionPayout?.toPositions ?? [];
		},
		enabled: !!props.locationId,
		staleTime: Infinity,
	});

	const stillLoading = isStillLoading(isLoading, isLoadingCommissionPayout, isLoadingSettingsCitCommission);

	if (isAllReady(Boolean(model), stillLoading)) {
		const { pcData, cbData } = extractConversionData(model);
		return (
			<Grid
				container
				direction="column"
				className={clsx({ "pb-[30px]": true, "shadow-white-section": !disableBoxShadow })}
				rowSpacing={0}
			>
				<EmployeeMetaSection
					{...props.employeeMetaProps}
					model={model}
					hidden={hideEmployeeMeta}
					loading={stillLoading}
					secondaryColumns={["DAYS_OUT", "RANKING"]}
				/>

				<FullWidthTablesSection
					{...props}
					stillLoading={stillLoading}
					citCommissionPayoutInitialPositions={citCommissionPayoutInitialPositions}
					systemSettingsCitCommissionPayout={systemSettingsCitCommissionPayout}
				/>

				<CardsDenseGrid
					loading={stillLoading}
					data={resolveCardsData({
						longevity: model?.longevity as LongevityInput,
						performanceMetrics: model?.performanceMetrics.performanceMetrics as PerformanceMetricsInput,
						deductions: model?.deductions?.deductions as unknown as DeductionItem[],
					})}
				/>

				<CallActivityAndCitSection {...props} stillLoading={stillLoading} />

				<ConversionsAndChargeBacksSection
					rawData={model?.productConversions}
					pcData={pcData}
					cbData={cbData}
					combined={Boolean(model?.productConversions.globalGrossAndUnits)}
					stillLoading={stillLoading}
					chargeBackData={chargeBackData}
					sharedId={model?._id}
					key={`${model?._id}-conversions-chargebacks-wrapper`}
				/>

				<Grid
					container
					direction="row"
					className={clsx({
						"pt-[15px] px-[30px] pb-[15px]": isAllReady(Boolean(model), stillLoading),
						"p-0": !isAllReady(Boolean(model), stillLoading),
					})}
				>
					<SpiffBonusSection {...props} />

					<Grid item className="w-[48px]" />

					<CompensationSection {...props} selected={selected} stillLoading={stillLoading} />
				</Grid>
			</Grid>
		);
	} else {
		return null;
	}
}
